// === grandmaster-frontend/frontend/src/components/Dashboard.js ===
// (MODIFIED - Fetch transcripts, display user info, add visibility controls)
import React, { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardHeader from "./DashboardHeader";
import RatingGraph from "./RatingGraph";
import { AuthContext, API_BASE_URL } from "../App";
import { WAITLIST_MODE, WAITLIST_URL } from "../config";
import "./Dashboard.css"; // Ensure CSS is imported
import { trackEvent } from "../utils/mixpanel";

// --- Transcript Manager Component ---
const TranscriptManager = ({
  transcript,
  onVisibilityChange,
  currentUserPlan,
}) => {
  const [currentVisibility, setCurrentVisibility] = useState(
    transcript.visibility
  );
  const [sharedInput, setSharedInput] = useState(
    (transcript.shared_with || []).join(", ")
  );
  const [showSharedInput, setShowSharedInput] = useState(
    transcript.visibility === "shared"
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const isPremium = currentUserPlan === "premium";
  const canSetPublic = true;
  const canSetShared = isPremium;

  // Update local state if transcript prop changes externally
  useEffect(() => {
    setCurrentVisibility(transcript.visibility);
    setSharedInput((transcript.shared_with || []).join(", "));
    setShowSharedInput(transcript.visibility === "shared");
  }, [transcript]);

  const onSelectChange = (e) => {
    const newVis = e.target.value;
    setError(""); // Clear previous errors
    setCurrentVisibility(newVis);
    setShowSharedInput(newVis === "shared");
    if (newVis !== "shared") {
      setSharedInput(""); // Clear input when switching away
    }
  };

  const onSave = async () => {
    setError(""); // Clear previous errors
    const usernames =
      currentVisibility === "shared"
        ? sharedInput
            .split(",")
            .map((u) => u.trim())
            .filter(Boolean)
        : [];

    if (currentVisibility === "shared" && usernames.length === 0 && isPremium) {
      setError("Please enter at least one username to share with.");
      return;
    }
    if (currentVisibility === "shared" && usernames.length > 50) {
      setError("Cannot share with more than 50 users.");
      return;
    }

    setIsSaving(true);
    try {
      await onVisibilityChange(transcript.id, currentVisibility, usernames);
      // Success message could be shown here or handled by parent
    } catch (err) {
      setError(err.message || "Failed to save."); // Set error state
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="transcript-manager">
      <label htmlFor={`visibility-select-${transcript.id}`}>Visibility:</label>
      <select
        id={`visibility-select-${transcript.id}`}
        value={currentVisibility}
        onChange={onSelectChange}
        // Disable options based on plan, not the whole select
      >
        <option value="private">Private (Only You)</option>
        <option value="public" disabled={!canSetPublic}>
          Public {!canSetPublic && "(Plus Required)"}
        </option>
        <option value="shared" disabled={!canSetShared}>
          Shared With Users {!canSetShared && "(Plus Required)"}
        </option>
      </select>

      {showSharedInput && isPremium && (
        <input
          type="text"
          placeholder="Lichess usernames, comma-separated"
          value={sharedInput}
          onChange={(e) => {
            setSharedInput(e.target.value);
            setError("");
          }}
          className="shared-input"
          aria-label="Usernames to share with"
        />
      )}
      <button
        onClick={onSave}
        className="save-visibility-btn"
        disabled={isSaving}
      >
        {isSaving ? "Saving..." : "Save"}
      </button>
      {error && (
        <p
          className="error"
          style={{
            color: "red",
            fontSize: "0.8em",
            width: "100%",
            textAlign: "right",
            margin: "5px 0 0 0",
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
};

const AnalysisRequestStatus = ({ request }) => {
  const getStatusStyle = (status) => {
    switch (status) {
      case "pending":
        return { color: "#ffc107" }; // Yellow
      case "in_progress":
        return { color: "#17a2b8" }; // Blue/Cyan
      case "completed":
        return { color: "#28a745" }; // Green
      case "failed":
        return { color: "#dc3545" }; // Red
      default:
        return { color: "#ccc" };
    }
  };

  return (
    <div className="analysis-request-card" style={styles.analysisRequestCard}>
      <p style={styles.analysisDetail}>
        <strong>Game:</strong> {request.game_key}
      </p>
      <p style={styles.analysisDetail}>
        <strong>Requested:</strong>{" "}
        {new Date(request.requested_at).toLocaleString()}
      </p>
      <p style={styles.analysisDetail}>
        <strong>Language:</strong>{" "}
        {request.language === "en" ? "English" : "Spanish"}
      </p>
      <p style={styles.analysisDetail}>
        <strong>Status:</strong>{" "}
        <span style={getStatusStyle(request.status)}>{request.status}</span>
      </p>
      {request.estimated_completion_at && (
        <p style={styles.analysisDetail}>
          <strong>ETA:</strong>{" "}
          {new Date(request.estimated_completion_at).toLocaleDateString()}
        </p>
      )}
      {request.notes && (
        <p style={styles.analysisDetail}>
          <strong style={{ display: "block", marginBottom: "3px" }}>
            Your Notes:
          </strong>{" "}
          <span style={{ fontStyle: "italic", color: "#ddd" }}>
            {request.notes}
          </span>
        </p>
      )}
      {/* Add link to completed PGN when available */}
      {request.status === "completed" && request.result_pgn_blob_url && (
        <a
          href={request.result_pgn_blob_url}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.downloadLink}
        >
          Download Annotated PGN
        </a>
      )}
    </div>
  );
};

// --- Dashboard Component ---
function Dashboard() {
  const { user, authToken, handleLogout, fetchUser, setUser } =
    useContext(AuthContext); // Use context
  const navigate = useNavigate();
  const [transcripts, setTranscripts] = useState([]);
  const [loadingTranscripts, setLoadingTranscripts] = useState(true);
  const [transcriptError, setTranscriptError] = useState(null);

  const [analysisRequests, setAnalysisRequests] = useState([]);
  const [loadingAnalysisRequests, setLoadingAnalysisRequests] = useState(true);
  const [analysisRequestError, setAnalysisRequestError] = useState(null);
  const [defaultVisibility, setDefaultVisibility] = useState(
    user.default_visibility
  );
  const [updatingDefault, setUpdatingDefault] = useState(false);

  window.postMessage(
    { type: "RESPONSE_AUTH_TOKEN", authToken: authToken },
    "*"
  );

  const handleDefaultVisibilityChange = async (e) => {
    const newVis = e.target.value;
    setUpdatingDefault(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/users/me/visibility-default`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ default_visibility: newVis }),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();

      // 1. update the local dropdown
      setDefaultVisibility(updated.default_visibility);

      // 2. merge into your context so the rest of the UI sees it
      setUser((u) => ({
        ...u,
        default_visibility: updated.default_visibility,
      }));
    } catch (err) {
      console.error("Could not update default visibility:", err);
      alert("Failed to save default visibility");
    } finally {
      setUpdatingDefault(false);
    }
  };

  // Fetch transcripts associated with the logged-in user
  const fetchTranscripts = useCallback(async () => {
    const start = Date.now();
    if (!authToken) return;
    setLoadingTranscripts(true);
    setTranscriptError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/transcripts`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        trackEvent("Transcripts Fetched", {
          count: data.length,
          duration_ms: Date.now() - start,
        });
        setTranscripts(data);
      } else {
        if (response.status === 401) {
          // Token likely expired, try refreshing user data which might trigger logout
          console.warn(
            "Transcript fetch unauthorized (401). Refreshing user session."
          );
          fetchUser(authToken); // Re-validate token / fetch user
        }
        const errorData = await response.text();
        throw new Error(
          `Failed to fetch transcripts: ${response.status} ${errorData}`
        );
      }
    } catch (err) {
      console.error("Fetch transcript error:", err);
      trackEvent("Transcripts Fetch Failed", {
        error: err.message,
        duration_ms: Date.now() - start,
      });
      setTranscriptError(err.message);
    } finally {
      setLoadingTranscripts(false);
    }
  }, [authToken, fetchUser]); // Added fetchUser dependency

  const fetchAnalysisRequests = useCallback(async () => {
    if (!authToken) return;
    setLoadingAnalysisRequests(true);
    setAnalysisRequestError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/analysis-requests/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAnalysisRequests(data);
      } else {
        if (response.status === 401) {
          fetchUser(authToken);
        }
        const errorData = await response.text();
        throw new Error(
          `Failed to fetch analysis requests: ${response.status} ${errorData}`
        );
      }
    } catch (err) {
      console.error("Fetch analysis requests error:", err);
      setAnalysisRequestError(err.message);
    } finally {
      setLoadingAnalysisRequests(false);
    }
  }, [authToken, fetchUser]);

  useEffect(() => {
    // Redirect if no user data after initial loading in App.js
    if (!user && !authToken) {
      navigate("/login?redirect=/dashboard");
      return;
    }
    // Fetch transcripts if user and token are available
    if (user && authToken) {
      fetchTranscripts();
      fetchAnalysisRequests();
    }
    // If only authToken exists, App.js useEffect is likely still fetching user
  }, [user, authToken, navigate, fetchTranscripts, fetchAnalysisRequests]);

  const handleUpgradeClick = () => {
    navigate("/pricing");
  };

  // Handle Visibility Change (passed to TranscriptManager)
  const handleVisibilityChange = useCallback(
    async (transcriptId, newVisibility, sharedUsernames = []) => {
      if (!authToken) return Promise.reject(new Error("Not authenticated"));

      const payload = {
        visibility: newVisibility,
        shared_with: newVisibility === "shared" ? sharedUsernames : null,
      };

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/transcripts/${transcriptId}/visibility`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          const updatedTranscript = await response.json();
          // Update local state immediately
          setTranscripts((prevTranscripts) =>
            prevTranscripts.map((t) =>
              t.id === transcriptId ? { ...t, ...updatedTranscript } : t
            )
          );
          console.log("Visibility updated successfully for", transcriptId);
          trackEvent("Visibility Changed", {
            transcriptId,
            newVisibility,
            sharedWith: sharedUsernames,
          });
          // Success state handled within TranscriptManager or could show global toast
          return Promise.resolve(); // Indicate success
        } else {
          const errorData = await response.json();
          console.error("Visibility update error response:", errorData);
          // Throw error to be caught by TranscriptManager
          throw new Error(
            errorData.detail ||
              `Failed to update visibility (${response.status})`
          );
        }
      } catch (err) {
        console.error("Network error updating visibility:", err);
        // Throw error to be caught by TranscriptManager
        throw new Error(
          err.message || "An error occurred while updating visibility."
        );
      }
    },
    [authToken]
  );

  // Render loading state if App.js hasn't loaded user yet
  if (!user) {
    return (
      <div className="dashboard-container">
        {/* Render header even while loading user */}
        <DashboardHeader />
        <div className="center" style={{ paddingTop: "80px" }}>
          Loading user data...
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <DashboardHeader />
      <div className="dashboard-content">
        {/* Sidebar placeholder */}
        {/* <aside className="dashboard-sidebar">...</aside> */}

        <main className="dashboard-main full-width">
          <section className="dashboard-welcome">
            <h1>Welcome, {user.lichess_username}!</h1>
            {user.plan === "free" && (
              <div className="upgrade-banner">
                <p>
                  Upgrade to Plus for Whisper STT, advanced sharing, and more!
                </p>
                <button onClick={handleUpgradeClick}>Upgrade Now</button>
              </div>
            )}
          </section>

          <section style={{ marginBottom: "30px" }}>
            <h2>Your Lichess Rating History</h2>
            <RatingGraph
              lichessUsername={user.lichess_username}
              authToken={authToken}
            />
          </section>

          <section className="dashboard-cards-section">
            <div className="card">
              <h3>Your Plan</h3>
              <p className={`plan-indicator ${user.plan}`}>{user.plan}</p>
              {user.plan === "free" && (
                <button onClick={handleUpgradeClick}>
                  View Upgrade Options
                </button>
              )}
              {user.plan === "premium" && (
                // Link to Stripe portal if implemented
                <button
                  onClick={() =>
                    alert(
                      "Manage subscription (Stripe Portal link coming soon!)"
                    )
                  }
                >
                  Manage Subscription
                </button>
              )}
            </div>
            <div className="card">
              <h3>Advance Transcription Usage</h3>
              {user.plan === "premium" ? (
                <>
                  <p>{user.paid_stt_usage_seconds} seconds used</p>
                  {/* Add quota display, e.g., from user object or config */}
                  {/* <p>Quota: 3600 seconds / month</p> */}
                </>
              ) : (
                <p>N/A (Upgrade for Whisper STT)</p>
              )}
            </div>
            <div className="card">
              <h3>Analysis Requests</h3>
              {user.plan === "premium" ? (
                <>
                  <p>
                    {user.remaining_analysis_requests} /{" "}
                    {ANALYSIS_REQUEST_LIMIT_PER_MONTH} remaining this month
                  </p>
                  {/* Optional: Add reset date info */}
                </>
              ) : (
                <p>N/A (Upgrade for Master Analysis)</p>
              )}
            </div>
            <div className="card">
              <h3>Default Visibility</h3>
              <select
                value={defaultVisibility}
                onChange={handleDefaultVisibilityChange}
                disabled={updatingDefault}
              >
                <option value="private">Private</option>
                <option value="public">Public</option>
                <option value="shared" disabled={user.plan === "free"}>
                  Shared {user.plan === "free" && "(Plus only)"}
                </option>
              </select>
            </div>
            {/* More stats cards if needed */}
          </section>
          <section
            className="dashboard-analysis-requests-section"
            style={styles.section}
          >
            <h2 style={styles.sectionTitle}>Master Analysis Requests</h2>
            {loadingAnalysisRequests && <p>Loading analysis requests...</p>}
            {analysisRequestError && (
              <p className="error">
                Error loading requests: {analysisRequestError}
              </p>
            )}
            {!loadingAnalysisRequests &&
              !analysisRequestError &&
              analysisRequests.length === 0 && (
                <p>
                  No analysis requests found. Plus users can request analysis
                  via the Lichess integration on game analysis pages.
                </p>
              )}
            {!loadingAnalysisRequests &&
              !analysisRequestError &&
              analysisRequests.length > 0 && (
                <div
                  className="analysis-requests-list"
                  style={styles.analysisRequestList}
                >
                  {analysisRequests.map((req) => (
                    <AnalysisRequestStatus key={req.id} request={req} />
                  ))}
                </div>
              )}
          </section>

          <section className="dashboard-transcripts-section">
            <h2>Synced Games / Transcripts</h2>
            {loadingTranscripts && <p>Loading transcripts...</p>}
            {transcriptError && (
              <p className="error">
                Error loading transcripts: {transcriptError}
              </p>
            )}
            {!loadingTranscripts &&
              !transcriptError &&
              transcripts.length === 0 && (
                <p>
                  No transcripts found. Use the GrandmasterAI Chrome Extension
                  while playing on Lichess or Chess.com (ensure Online Mode is
                  enabled in extension settings) to sync transcripts.
                </p>
              )}
            {!loadingTranscripts &&
              !transcriptError &&
              transcripts.length > 0 && (
                <div className="transcripts-list">
                  {transcripts.map((t) => (
                    <div key={t.id} className="transcript-card">
                      <h4>Game Key: {t.game_key}</h4>
                      <p>Side: {t.side}</p>
                      <p>Synced: {new Date(t.created_at).toLocaleString()}</p>
                      <a
                        // Determine URL based on game key pattern or stored site info
                        href={
                          t.game_key.length === 8
                            ? `https://lichess.org/${t.game_key}`
                            : `https://www.chess.com/game/live/${t.game_key}` /* Basic Guess */
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-link"
                      >
                        View Original Game
                      </a>
                      {/* Pass necessary props to manager */}
                      <TranscriptManager
                        transcript={t}
                        onVisibilityChange={handleVisibilityChange}
                        currentUserPlan={user.plan}
                      />
                    </div>
                  ))}
                </div>
              )}
          </section>
        </main>
      </div>
    </div>
  );
}

const ANALYSIS_REQUEST_LIMIT_PER_MONTH = 3;

// Basic inline styles for the new section (or move to Dashboard.css)
const styles = {
  section: {
    marginBottom: "30px",
  },
  sectionTitle: {
    fontSize: "1.8rem",
    marginBottom: "15px",
    color: "#eee",
    borderBottom: "1px solid #444",
    paddingBottom: "8px",
  },
  analysisRequestList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "15px",
  },
  analysisRequestCard: {
    background: "#3a3a3a", // Slightly different background
    padding: "15px",
    borderRadius: "6px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  analysisDetail: {
    margin: 0,
    fontSize: "0.9rem",
    color: "#ccc",
    lineHeight: 1.4,
  },
  downloadLink: {
    color: "#61dafb",
    textDecoration: "none",
    fontWeight: "bold",
    marginTop: "5px",
    alignSelf: "flex-start",
  },
};

export default Dashboard;
